<template>
   <div class="spark-activity-container px-3 py-3">
      <!-- Activity Controls Header -->
      <div class="spark-activity-controls mb-3">
         <!-- Search and Refresh Row -->
         <div class="d-flex align-items-center gap-2 mb-2">
            <div class="spark-search-wrapper flex-grow-1">
               <i class="fa-solid fa-magnifying-glass spark-search-icon"></i>
               <input v-model.trim="search" type="text"
                  class="spark-search-input form-control form-control-sm shadow-none pe-4"
                  placeholder="Search contract logs, users, signers..." @input="onSearch" />
               <button v-if="search" type="button" class="spark-search-clear-btn" @click="clearSearch" title="Clear search">
                  <i class="fa-solid fa-xmark"></i>
               </button>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 spark-btn-refresh"
               @click="refreshActivities" :disabled="isLoading" title="Refresh Activity Logs">
               <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': isLoading }"></i>
               <span class="d-none d-sm-inline">Refresh</span>
            </button>
         </div>

         <!-- Secondary Filters: Modern Filter Bar (Specific Activity Popover + Date Range + Active Filter Chips) -->
         <div class="modern-filter-bar d-flex flex-wrap align-items-center gap-2 pt-1">
            <!-- Specific Activity Multi-Select Popover Dropdown -->
            <div class="dropdown">
               <button type="button" class="btn modern-filter-btn" :class="{ 'has-active': selectedActivities.length > 0 }" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                  <i class="fa-solid fa-sliders filter-icon"></i>
                  <span>Specific Activity</span>
                  <span v-if="selectedActivities.length > 0" class="filter-count-badge">
                     {{ selectedActivities.length }}
                  </span>
                  <i class="fa-solid fa-chevron-down ms-2 chevron-icon"></i>
               </button>
               <div class="dropdown-menu modern-dropdown-menu p-2" @click.stop>
                  <div class="dropdown-header-custom d-flex justify-content-between align-items-center mb-2 px-2">
                     <span class="fw-bold text-uppercase text-muted" style="font-size: 0.7rem; letter-spacing: 0.05em;">Filter by Activity</span>
                     <button v-if="selectedActivities.length > 0" type="button" class="btn btn-link p-0 text-decoration-none" style="font-size: 0.75rem; color: #00A0C1;" @click="selectedActivities = []">Reset</button>
                  </div>
                  <div class="dropdown-options-list">
                     <label v-for="activity in selectableActivityOptions" :key="activity.value" class="modern-checkbox-label">
                        <input type="checkbox" :value="activity.value" v-model="selectedActivities">
                        <span class="custom-chk"></span>
                        <span class="status-dot" :class="getActivityDotClass(activity.value)"></span>
                        <span class="option-text text-truncate">{{ activity.text }}</span>
                     </label>
                  </div>
               </div>
            </div>

            <!-- Date Range Filter Control -->
            <div class="modern-date-filter-box position-relative d-inline-flex align-items-center" :class="{ 'has-active': hasDateFilter }">
               <i class="fa-regular fa-calendar filter-icon"></i>
               <datepicker
                  inputClassName="dp-custom-input modern-dp-input"
                  menuClassName="dp-custom-menu"
                  v-model="filterByDate"
                  placeholder="Date Range"
                  :enableTimePicker="false"
                  :hideInputIcon="true"
                  :teleport="true"
                  range
                  :clearable="false"
                  autoApply
               />
               <i v-if="!hasDateFilter" class="fa-solid fa-chevron-down chevron-icon pe-none"></i>
               <button
                  v-else
                  type="button"
                  class="spark-date-clear-btn"
                  title="Clear date filter"
                  @click.stop="filterByDate = null"
               >
                  <i class="fa-solid fa-xmark"></i>
               </button>
            </div>

            <!-- Active Filter Chips -->
            <div v-if="hasActiveFilters" class="active-filters-container d-flex flex-wrap gap-2 align-items-center ms-md-1">
               <div class="vr mx-1 text-muted opacity-25 d-none d-md-block" style="height: 20px;"></div>
               <span v-for="actVal in selectedActivities" :key="'chip-act-' + actVal" class="filter-chip">
                  {{ getActivityOptionText(actVal) }}
                  <i class="fa-solid fa-xmark ms-1" @click="removeSelectedActivity(actVal)"></i>
               </span>
               <span v-if="hasDateFilter" class="filter-chip">
                  {{ formattedDateRange }}
                  <i class="fa-solid fa-xmark ms-1" @click="filterByDate = null"></i>
               </span>
               <span v-if="search !== ''" class="filter-chip">
                  Search: "{{ search }}"
                  <i class="fa-solid fa-xmark ms-1" @click="clearSearch()"></i>
               </span>
               <button type="button" class="btn btn-link reset-all-btn p-0 ms-1" @click="clearAllFilters()">
                  Reset All
               </button>
            </div>
         </div>
      </div>

      <!-- Loading Skeleton State -->
      <div v-if="isLoading && contractActivities.length === 0" class="spark-activity-skeleton py-3">
         <div v-for="n in 3" :key="n" class="spark-skeleton-item mb-3">
            <div class="spark-skeleton-dot"></div>
            <div class="spark-skeleton-card">
               <div class="spark-skeleton-line short mb-2"></div>
               <div class="spark-skeleton-line mb-2"></div>
               <div class="spark-skeleton-line half"></div>
            </div>
         </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredActivities.length === 0" class="spark-activity-empty text-center py-5">
         <div class="spark-activity-empty__icon mb-3">
            <i class="fa-solid fa-clock-rotate-left"></i>
         </div>
         <h6 class="fw-bold text-dark mb-1">No Activity Records Found</h6>
         <p class="text-muted small mb-3">
            {{ hasActiveFilters ? 'No activities match the current filters or date range.' : 'No activity logs have been recorded for this contract yet.' }}
         </p>
         <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-outline-primary" @click="clearAllFilters">
            Clear Active Filters
         </button>
      </div>

      <!-- Modern Activity Timeline Feed -->
      <div v-else class="spark-activity-feed">
         <div v-for="group in groupedActivities" :key="group.dateKey" class="spark-activity-group">
            <!-- Group Date Header Badge -->
            <div class="spark-activity-group__header">
               <span class="spark-activity-group__badge">
                  <i class="fa-regular fa-calendar me-1"></i> {{ group.dateLabel }}
               </span>
            </div>

            <!-- Group Activity Items -->
            <div class="spark-activity-group__items">
               <div v-for="item in group.items" :key="item.id || item.date_created" 
                  class="spark-activity-item" :class="getActivityMeta(item.activity_type).itemClass">
                  
                  <!-- Timeline Node with Activity Icon -->
                  <div class="spark-activity-node" :class="getActivityMeta(item.activity_type).nodeClass"
                     :title="getActivityMeta(item.activity_type).label">
                     <i :class="getActivityMeta(item.activity_type).icon"></i>
                  </div>

                  <!-- Activity Item Card -->
                  <div class="spark-activity-card">
                     <!-- Card Top Row: Title, Pill & Relative Time -->
                     <div class="spark-activity-card__header d-flex align-items-center justify-content-between flex-wrap gap-1">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="spark-activity-card__title">
                              {{ item.activity_title || getActivityMeta(item.activity_type).label }}
                           </span>
                           <span class="spark-activity-badge" :class="getActivityMeta(item.activity_type).badgeClass">
                              {{ getActivityMeta(item.activity_type).shortLabel }}
                           </span>
                        </div>
                        <span class="spark-activity-time text-muted small" :title="getExactDateTime(item.date_created)">
                           <i class="fa-regular fa-clock me-1 opacity-75"></i> {{ getRelativeTime(item.date_created) }}
                        </span>
                     </div>

                     <!-- Author / Actor Row -->
                     <div class="spark-activity-actor d-flex align-items-center gap-2 mt-1 mb-2">
                        <div class="spark-actor-avatar" :class="getActorAvatarClass(item)">
                           <span>{{ getActorInitials(item) }}</span>
                        </div>
                        <span class="spark-actor-name small text-secondary">
                           {{ getActorName(item) }}
                        </span>
                     </div>

                     <!-- Activity Details Content Body -->
                     <div v-if="item.activity_details" class="spark-activity-details">
                        <!-- Smart Render for Status Transitions -->
                        <div v-if="isStatusChangeDetails(item)" class="spark-status-transition-box mb-1">
                           <span class="spark-status-badge from">{{ parseStatusTransition(item.activity_details).from }}</span>
                           <i class="fa-solid fa-arrow-right-long text-muted mx-1"></i>
                           <span class="spark-status-badge to">{{ parseStatusTransition(item.activity_details).to }}</span>
                        </div>

                        <!-- Render Details Text -->
                        <div class="spark-activity-details__text" :class="{ 'is-quote': isQuoteDetails(item) }">
                           <p class="mb-0">{{ item.activity_details }}</p>
                        </div>
                     </div>

                     <!-- Activity Footer / Timestamp info -->
                     <div class="spark-activity-footer d-flex align-items-center justify-content-between mt-2 pt-1 border-top">
                        <span class="spark-exact-time text-muted">
                           {{ formatExactTime(item.date_created) }}
                        </span>
                        <div class="d-flex align-items-center gap-2">
                           <span v-if="item.date_updated && item.date_updated !== item.date_created" 
                              class="spark-edited-flag text-muted" :title="'Updated: ' + getExactDateTime(item.date_updated)">
                              <i class="fa-solid fa-pen-nib me-1"></i> Edited
                           </span>
                           <button v-if="item.activity_details" type="button" class="spark-btn-copy-sm" 
                              @click="copyToClipboard(item.activity_details, 'Activity Details')" title="Copy text">
                              <i class="fa-regular fa-copy"></i>
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import moment from "moment";
import { debounce } from '@/plugins/helper.js'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import '@/assets/css/vue-datetimepicker.css'

export default {
   name: 'ContractActivity',

   components: {
      Datepicker
   },

   data() {
      return {
         search: "",
         contractActivities: [],
         isLoading: false,
         activeCategory: 'all',
         filterByActivity: "none",
         selectedActivities: [],
         filterByUser: "none",
         filterByDate: null,
         contractID: 0,
         vtxUsers: [],
         debounceFn: null,
         activities: [
            { id: "contract_create", display_name: "Contract Creation", category: "edits" },
            { id: "contract_update", display_name: "Contract Updated", category: "edits" },
            { id: "contract_status_change", display_name: "Status Change & Approvals", category: "status" },
            { id: "contract_sent", display_name: "Contract Sent", category: "dispatches" },
            { id: "contract_resent", display_name: "Contract Resent", category: "dispatches" },
            { id: "contract_shared", display_name: "Contract Shared", category: "dispatches" },
            { id: "contract_revoked", display_name: "Access Revoked", category: "dispatches" },
            { id: "contract_accepted", display_name: "Contract Executed / Accepted", category: "status" },
            { id: "contract_declined", display_name: "Contract Declined", category: "status" },
            { id: "contract_question", display_name: "Client Revisions & Questions", category: "revisions" },
            { id: "contract_extended", display_name: "Validity Extended", category: "revisions" },
            { id: "contract_reissue", display_name: "Reissue Request", category: "revisions" },
            { id: "contract_expired", display_name: "Contract Expired", category: "status" },
         ],
      }
   },

   created() {
      this.getVertexUsers();
      this.debounceFn = debounce((contractID) => this.getContractActivities(contractID), 300);
   },

   computed: {
      filterByActivityList() {
         return [
            { value: 'none', text: 'All Activity Types' },
            ...this.activities.map(a => ({ value: a.id, text: a.display_name, category: a.category }))
         ];
      },

      selectableActivityOptions() {
         return this.filterByActivityList.filter(a => a.value !== 'none');
      },

      hasDateFilter() {
         return Array.isArray(this.filterByDate) && Boolean(this.filterByDate[0]);
      },

      formattedDateRange() {
         if (!this.hasDateFilter) return '';
         const start = moment(this.filterByDate[0]).format('DD MMM YYYY');
         const end = this.filterByDate[1] ? moment(this.filterByDate[1]).format('DD MMM YYYY') : start;
         return start === end ? start : `${start} - ${end}`;
      },

      hasActiveFilters() {
         return this.selectedActivities.length > 0 || 
                this.hasDateFilter || 
                this.search !== "";
      },

      categoryCounts() {
         const counts = { dispatches: 0, status: 0, revisions: 0, edits: 0 };
         this.contractActivities.forEach(item => {
            const type = item.activity_type || '';
            if (type.includes('sent') || type.includes('resent') || type.includes('shared') || type.includes('revoked')) {
               counts.dispatches++;
            } else if (type.includes('status') || type.includes('accepted') || type.includes('declined') || type.includes('expired')) {
               counts.status++;
            } else if (type.includes('question') || type.includes('extended') || type.includes('reissue')) {
               counts.revisions++;
            } else if (type.includes('create') || type.includes('update')) {
               counts.edits++;
            }
         });
         return counts;
      },

      filteredActivities() {
         let items = [...this.contractActivities];

         // Category Filter
         if (this.activeCategory !== 'all') {
            items = items.filter(item => {
               const type = item.activity_type || '';
               if (this.activeCategory === 'dispatches') {
                  return type.includes('sent') || type.includes('resent') || type.includes('shared') || type.includes('revoked');
               }
               if (this.activeCategory === 'status') {
                  return type.includes('status') || type.includes('accepted') || type.includes('declined') || type.includes('expired');
               }
               if (this.activeCategory === 'revisions') {
                  return type.includes('question') || type.includes('extended') || type.includes('reissue');
               }
               if (this.activeCategory === 'edits') {
                  return type.includes('create') || type.includes('update');
               }
               return true;
            });
         }

         // Specific Activity Multi-Select Filter
         if (this.selectedActivities.length > 0) {
            items = items.filter(item => {
               const type = item.activity_type || '';
               return this.selectedActivities.some(sel => type.includes(sel));
            });
         }

         return items;
      },

      groupedActivities() {
         const groupsMap = {};

         this.filteredActivities.forEach(item => {
            const rawDate = item.date_created;
            const dateObj = moment(rawDate);
            const dateKey = dateObj.isValid() ? dateObj.format('YYYY-MM-DD') : 'Unknown Date';

            let dateLabel = dateKey;
            if (dateObj.isValid()) {
               const today = moment().format('YYYY-MM-DD');
               const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
               if (dateKey === today) {
                  dateLabel = 'Today';
               } else if (dateKey === yesterday) {
                  dateLabel = 'Yesterday';
               } else {
                  dateLabel = dateObj.format('DD MMMM YYYY');
               }
            }

            if (!groupsMap[dateKey]) {
               groupsMap[dateKey] = {
                  dateKey,
                  dateLabel,
                  items: []
               };
            }

            item.activity_title = this.resolveActivityTitle(item.activity_type);
            groupsMap[dateKey].items.push(item);
         });

         // Sort dates descending
         return Object.keys(groupsMap)
            .sort((a, b) => b.localeCompare(a))
            .map(key => groupsMap[key]);
      }
   },

   watch: {
      filterByUser() {
         this.triggerDebounce();
      },
      filterByActivity() {
         this.triggerDebounce();
      },
      filterByDate() {
         this.triggerDebounce();
      },
      selectedActivities: {
         handler() {
            this.triggerDebounce();
         },
         deep: true
      }
   },

   methods: {
      init(id) {
         this.contractID = id;
         this.filterByDate = null;
         this.search = "";
         this.activeCategory = 'all';
         this.filterByActivity = "none";
         this.selectedActivities = [];
         this.filterByUser = "none";
         this.contractActivities = [];
         this.getContractActivities(id);
      },

      refreshActivities() {
         if (this.contractID) {
            this.getContractActivities(this.contractID);
         }
      },

      triggerDebounce() {
         if (this.contractID && this.debounceFn) {
            this.debounceFn(this.contractID);
         }
      },

      onSearch() {
         this.triggerDebounce();
      },

      clearSearch() {
         this.search = "";
         this.triggerDebounce();
      },

      setCategory(cat) {
         this.activeCategory = cat;
      },

      removeSelectedActivity(val) {
         this.selectedActivities = this.selectedActivities.filter(v => v !== val);
      },

      getActivityOptionText(val) {
         const match = this.activities.find(a => a.id === val);
         return match ? match.display_name : val;
      },

      getCategoryLabel(cat) {
         const map = {
            dispatches: 'Dispatches & Sharing',
            status: 'Status Change & Approvals',
            revisions: 'Client Revisions & Questions',
            edits: 'Updates & Content'
         };
         return map[cat] || cat;
      },

      getActivityDotClass(val = '') {
         if (val.includes('create')) return 'dot-create';
         if (val.includes('sent') || val.includes('resent') || val.includes('shared')) return 'dot-sent';
         if (val.includes('accepted')) return 'dot-accepted';
         if (val.includes('declined') || val.includes('revoked') || val.includes('expired')) return 'dot-declined';
         if (val.includes('question') || val.includes('extended') || val.includes('reissue')) return 'dot-question';
         if (val.includes('status') || val.includes('update')) return 'dot-status';
         return 'dot-status';
      },

      clearAllFilters() {
         this.search = "";
         this.activeCategory = 'all';
         this.filterByActivity = "none";
         this.selectedActivities = [];
         this.filterByUser = "none";
         this.filterByDate = null;
         this.triggerDebounce();
      },

      async getContractActivities(id) {
         this.isLoading = true;
         let apiPath = "/contracts/" + id + "/activities?";

         if (this.search !== "") {
            apiPath += "search=" + encodeURIComponent(this.search);
         }

         if (this.selectedActivities.length === 1) {
            apiPath += "&activity=" + encodeURIComponent(this.selectedActivities[0]);
         } else if (this.filterByActivity !== "" && this.filterByActivity !== "none") {
            apiPath += "&activity=" + encodeURIComponent(this.filterByActivity);
         }

         if (this.filterByUser !== "" && this.filterByUser !== "none") {
            apiPath += "&user=" + encodeURIComponent(this.filterByUser);
         }

         if (this.hasDateFilter) {
            const from = this.convertToUTC(this.filterByDate[0]);
            const to = this.filterByDate[1] ? this.convertToUTC(this.filterByDate[1]) : from;
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            apiPath += "&search_date_from=" + encodeURIComponent(from) + "&search_date_to=" + encodeURIComponent(to) + "&search_tz=" + encodeURIComponent(tz);
         }

         try {
            const response = await this.$axios.get(apiPath);
            this.contractActivities = response.data.items || [];
            this.resolveUserNames();
         } catch (error) {
            this.$toast.error(error?.response?.data?.message || 'Failed to load contract activity logs');
         } finally {
            this.isLoading = false;
         }
      },

      convertToUTC(timestamp) {
         const date = new Date(timestamp);
         return date.toISOString().split('T')[0];
      },

      getVertexUsers() {
         this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || [];
      },

      resolveUserNames() {
         this.contractActivities.forEach((item, index) => {
            const userId = item.created_by;
            let user = Array.isArray(this.vtxUsers) ? this.vtxUsers.find(u => u && (u.id === userId || u.user?.id === userId)) : null;

            if (user) {
               const uObj = user.user || user;
               this.contractActivities[index].user = {
                  firstName: uObj.first_name || '',
                  lastName: uObj.last_name || ''
               };
            } else if (!userId || userId === 0) {
               this.contractActivities[index].user = {
                  firstName: "Spark",
                  lastName: "System"
               };
            } else {
               this.contractActivities[index].user = {
                  firstName: "User #" + userId,
                  lastName: ""
               };
            }
         });
      },

      resolveActivityTitle(type = '') {
         if (type.includes("contract_create")) return "Contract Created";
         if (type.includes("contract_update")) return "Contract Content Updated";
         if (type.includes("contract_sent")) return "Contract Sent to Client";
         if (type.includes("contract_resent")) return "Contract Resent";
         if (type.includes("contract_status_change")) return "Status Changed";
         if (type.includes("contract_shared")) return "Contract Shared with Signers";
         if (type.includes("contract_revoked")) return "Access Revoked";
         if (type.includes("contract_question")) return "Client Feedback & Section Revisions";
         if (type.includes("contract_accepted")) return "Contract Executed & Accepted";
         if (type.includes("contract_declined")) return "Contract Declined";
         if (type.includes("contract_extended")) return "Contract Expiry Extended";
         if (type.includes("contract_reissue")) return "Contract Reissue Requested";
         if (type.includes("contract_expired")) return "Contract Expired";
         return "Contract Activity";
      },

      getActivityMeta(type = '') {
         if (type.includes("contract_create")) {
            return {
               label: "Contract Created",
               shortLabel: "Created",
               icon: "fa-solid fa-file-contract",
               nodeClass: "spark-node--create",
               badgeClass: "spark-badge--create",
               itemClass: "spark-item--create"
            };
         }
         if (type.includes("contract_update")) {
            return {
               label: "Content Updated",
               shortLabel: "Updated",
               icon: "fa-solid fa-pen-to-square",
               nodeClass: "spark-node--update",
               badgeClass: "spark-badge--update",
               itemClass: "spark-item--update"
            };
         }
         if (type.includes("contract_sent") || type.includes("contract_resent")) {
            return {
               label: "Contract Sent",
               shortLabel: "Sent",
               icon: "fa-solid fa-paper-plane",
               nodeClass: "spark-node--sent",
               badgeClass: "spark-badge--sent",
               itemClass: "spark-item--sent"
            };
         }
         if (type.includes("contract_shared")) {
            return {
               label: "Shared Access",
               shortLabel: "Shared",
               icon: "fa-solid fa-share-nodes",
               nodeClass: "spark-node--shared",
               badgeClass: "spark-badge--shared",
               itemClass: "spark-item--shared"
            };
         }
         if (type.includes("contract_revoked")) {
            return {
               label: "Access Revoked",
               shortLabel: "Revoked",
               icon: "fa-solid fa-user-xmark",
               nodeClass: "spark-node--revoked",
               badgeClass: "spark-badge--revoked",
               itemClass: "spark-item--revoked"
            };
         }
         if (type.includes("contract_accepted")) {
            return {
               label: "Contract Executed",
               shortLabel: "Executed",
               icon: "fa-solid fa-circle-check",
               nodeClass: "spark-node--accepted",
               badgeClass: "spark-badge--accepted",
               itemClass: "spark-item--accepted"
            };
         }
         if (type.includes("contract_declined")) {
            return {
               label: "Contract Declined",
               shortLabel: "Declined",
               icon: "fa-solid fa-circle-xmark",
               nodeClass: "spark-node--declined",
               badgeClass: "spark-badge--declined",
               itemClass: "spark-item--declined"
            };
         }
         if (type.includes("contract_question")) {
            return {
               label: "Client Revisions & Questions",
               shortLabel: "Revisions",
               icon: "fa-solid fa-comment-dots",
               nodeClass: "spark-node--question",
               badgeClass: "spark-badge--question",
               itemClass: "spark-item--question"
            };
         }
         if (type.includes("contract_extended")) {
            return {
               label: "Validity Extended",
               shortLabel: "Extended",
               icon: "fa-solid fa-calendar-plus",
               nodeClass: "spark-node--extended",
               badgeClass: "spark-badge--extended",
               itemClass: "spark-item--extended"
            };
         }
         if (type.includes("contract_reissue")) {
            return {
               label: "Reissue Request",
               shortLabel: "Reissue",
               icon: "fa-solid fa-rotate-left",
               nodeClass: "spark-node--reissue",
               badgeClass: "spark-badge--reissue",
               itemClass: "spark-item--reissue"
            };
         }
         if (type.includes("contract_expired")) {
            return {
               label: "Contract Expired",
               shortLabel: "Expired",
               icon: "fa-solid fa-clock-rotate-left",
               nodeClass: "spark-node--expired",
               badgeClass: "spark-badge--expired",
               itemClass: "spark-item--expired"
            };
         }
         return {
            label: "Status Changed",
            shortLabel: "Status",
            icon: "fa-solid fa-arrows-rotate",
            nodeClass: "spark-node--status",
            badgeClass: "spark-badge--status",
            itemClass: "spark-item--status"
         };
      },

      getRelativeTime(date) {
         if (!date) return '';
         return moment(date).fromNow();
      },

      getExactDateTime(date) {
         if (!date) return '';
         return moment(date).format('DD MMMM YYYY, h:mm:ss A');
      },

      formatExactTime(date) {
         if (!date) return '';
         return moment(date).format('h:mm A • DD-MMM-YYYY');
      },

      getActorName(item) {
         if (item.user && (item.user.firstName || item.user.lastName)) {
            return `${item.user.firstName} ${item.user.lastName}`.trim();
         }
         return 'Spark Automation';
      },

      getActorInitials(item) {
         if (item.user && item.user.firstName) {
            const first = item.user.firstName.charAt(0);
            const last = item.user.lastName ? item.user.lastName.charAt(0) : '';
            return (first + last).toUpperCase();
         }
         return 'SP';
      },

      getActorAvatarClass(item) {
         if (!item.created_by || item.created_by === 0) {
            return 'spark-actor-avatar--system';
         }
         return 'spark-actor-avatar--user';
      },

      isStatusChangeDetails(item) {
         const type = item.activity_type || '';
         const details = item.activity_details || '';
         return type.includes('status') && (details.includes(' to ') || details.includes(' -> '));
      },

      parseStatusTransition(details = '') {
         const parts = details.split(/ to | -> /i);
         return {
            from: parts[0]?.trim() || 'Previous',
            to: parts[1]?.trim() || 'New'
         };
      },

      isQuoteDetails(item) {
         const type = item.activity_type || '';
         return type.includes('question') || type.includes('declined') || type.includes('feedback');
      },

      copyToClipboard(text, label = 'Copied') {
         if (!text) return;
         navigator.clipboard.writeText(text).then(() => {
            this.$toast.success(`${label} copied to clipboard`);
         }).catch(() => {
            this.$toast.info('Copied to clipboard');
         });
      }
   }
}
</script>

<style lang="scss" scoped>
.spark-activity-container {
   font-family: inherit;
   max-width: 100%;
}

/* Controls */
.spark-search-wrapper {
   position: relative;
   display: flex;
   align-items: center;

   .spark-search-icon {
      position: absolute;
      left: 10px;
      color: #94a3b8;
      font-size: 0.8125rem;
      pointer-events: none;
   }

   .spark-search-input {
      padding-left: 30px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      font-size: 0.8125rem;
      transition: all 0.2s ease;

      &:focus {
         background: #ffffff;
         border-color: #00A0C1;
         box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
      }
   }

   .spark-search-clear-btn {
      position: absolute;
      right: 8px;
      background: none;
      border: none;
      color: #94a3b8;
      font-size: 0.75rem;
      padding: 2px 4px;
      cursor: pointer;

      &:hover {
         color: #475569;
      }
   }
}

.spark-btn-refresh {
   border-radius: 8px;
   font-size: 0.8125rem;
   padding: 0.35rem 0.65rem;
   border-color: #e2e8f0;
   background: #ffffff;
   color: #475569;

   &:hover:not(:disabled) {
      background: #f1f5f9;
      border-color: #cbd5e1;
      color: #00A0C1;
   }
}

.spark-filter-label {
   font-size: 0.6875rem;
   text-transform: uppercase;
   letter-spacing: 0.04em;
}

.spark-date-clear-btn {
   position: absolute;
   right: 8px;
   top: 50%;
   transform: translateY(-50%);
   background: none;
   border: none;
   color: #94a3b8;
   font-size: 0.75rem;
   padding: 2px;
   z-index: 5;

   &:hover {
      color: #ef4444;
   }
}

/* Category Filter Pills */
.spark-filter-pills-row {
   overflow-x: auto;
   padding-bottom: 2px;

   &::-webkit-scrollbar {
      height: 4px;
   }
   &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
   }
}

.spark-filter-pill {
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 4px 10px;
   font-size: 0.75rem;
   font-weight: 500;
   border-radius: 9999px;
   border: 1px solid #e2e8f0;
   background: #f8fafc;
   color: #64748b;
   cursor: pointer;
   transition: all 0.15s ease;
   white-space: nowrap;

   &:hover {
      background: #f1f5f9;
      color: #1e293b;
      border-color: #cbd5e1;
   }

   &.is-active {
      background: #e6f7fa;
      border-color: #00A0C1;
      color: #008298;
      font-weight: 600;

      .spark-filter-pill__count {
         background: #00A0C1;
         color: #ffffff;
      }
   }

   .spark-filter-pill__count {
      font-size: 0.6875rem;
      padding: 1px 6px;
      border-radius: 10px;
      background: #e2e8f0;
      color: #475569;
   }
}

/* ── Modern Filter Bar Styles ── */
.modern-filter-bar {
   width: 100%;
}

.modern-filter-btn {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   background: #ffffff;
   color: #334155;
   font-weight: 600;
   font-size: 0.8125rem;
   padding: 0 12px;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   transition: all 0.2s ease;

   &:hover,
   &[aria-expanded="true"] {
      border-color: #00A0C1;
      background: #f8fafc;
      color: #00A0C1;
   }

   &.has-active {
      background: #e6f7fa;
      border-color: #00A0C1;
      color: #008298;
   }

   .filter-icon {
      color: #00A0C1;
      font-size: 0.85rem;
   }

   .chevron-icon {
      font-size: 0.72rem;
      color: #94a3b8;
   }
}

.filter-count-badge {
   background: #00A0C1;
   color: #ffffff;
   font-size: 0.7rem;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 8px;
}

.modern-dropdown-menu {
   min-width: 260px;
   border-radius: 12px;
   border: none;
   box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
   background: #ffffff;
   z-index: 1060;
}

.dropdown-options-list {
   max-height: 250px;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 2px;
}

.modern-checkbox-label {
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 6px 8px;
   border-radius: 6px;
   cursor: pointer;
   font-size: 0.8125rem;
   color: #334155;
   transition: background 0.15s ease;
   user-select: none;
   position: relative;
   margin-bottom: 0;

   &:hover {
      background: #f1f5f9;
   }

   input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
   }

   .custom-chk {
      width: 16px;
      height: 16px;
      background-color: #ffffff;
      border: 1.5px solid #cbd5e1;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      flex-shrink: 0;
      position: relative;
   }

   &:hover input[type="checkbox"]:not(:checked) ~ .custom-chk {
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk {
      background-color: #00A0C1;
      border-color: #00A0C1;
   }

   input[type="checkbox"]:checked ~ .custom-chk::after {
      content: "";
      width: 4px;
      height: 8px;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      margin-bottom: 1px;
   }

   input[type="checkbox"]:focus ~ .custom-chk {
      box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.2);
   }
}

.status-dot {
   width: 8px;
   height: 8px;
   border-radius: 50%;
   display: inline-block;
   flex-shrink: 0;

   &.dot-create { background-color: #10b981; }
   &.dot-sent { background-color: #00A0C1; }
   &.dot-accepted { background-color: #059669; }
   &.dot-declined { background-color: #ef4444; }
   &.dot-question { background-color: #f59e0b; }
   &.dot-status { background-color: #6366f1; }
}

.modern-date-filter-box {
   height: 38px;
   padding: 0 12px;
   background: #ffffff;
   border: 1px solid #cbd5e1;
   border-radius: 8px;
   gap: 6px;
   transition: all 0.2s ease;
   cursor: pointer;

   .filter-icon {
      color: #64748b;
      font-size: 0.85rem;
      flex-shrink: 0;
   }

   .chevron-icon {
      font-size: 0.7rem;
      color: #94a3b8;
      margin-left: 2px;
      flex-shrink: 0;
   }

   &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
   }

   &.has-active {
      background: #e6f7fa;
      border-color: #00A0C1;
      color: #008298;

      .filter-icon {
         color: #00A0C1;
      }

      :deep(.modern-dp-input) {
         color: #008298 !important;
         width: 185px;
      }
   }

   :deep(.dp__main) {
      width: auto;
   }

   :deep(.dp__input_icon),
   :deep(.dp__clear_icon) {
      display: none !important;
   }

   :deep(.modern-dp-input) {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      height: 36px;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #334155;
      width: 82px;
      cursor: pointer;

      &::placeholder {
         color: #334155;
         font-weight: 600;
         opacity: 1;
      }
   }

   .spark-date-clear-btn {
      background: transparent;
      border: none;
      color: #008298;
      padding: 2px 4px;
      font-size: 0.75rem;
      cursor: pointer;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;

      &:hover {
         color: #ef4444;
         background: rgba(239, 68, 68, 0.08);
      }
   }
}

.filter-chip {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid #b3e5ef;
   padding: 3px 10px;
   border-radius: 16px;
   font-size: 0.75rem;
   font-weight: 600;

   i {
      font-size: 0.7rem;
      cursor: pointer;
      transition: color 0.15s;

      &:hover {
         color: #ef4444;
      }
   }
}

.reset-all-btn {
   color: #00A0C1;
   font-size: 0.75rem;
   font-weight: 700;
   text-decoration: none;

   &:hover {
      color: #008298;
      text-decoration: underline;
   }
}

/* Feed Container */
.spark-activity-feed {
   position: relative;
   padding-left: 20px;

   &::before {
      content: '';
      position: absolute;
      left: 17px;
      top: 10px;
      bottom: 20px;
      width: 2px;
      background: #e2e8f0;
   }
}

.spark-activity-group {
   position: relative;
   margin-bottom: 1.5rem;

   &:last-child {
      margin-bottom: 0.5rem;
   }
}

.spark-activity-group__header {
   position: relative;
   margin-bottom: 12px;
   padding-left: 12px;

   .spark-activity-group__badge {
      display: inline-flex;
      align-items: center;
      padding: 3px 10px;
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-radius: 6px;
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
   }
}

.spark-activity-group__items {
   display: flex;
   flex-direction: column;
   gap: 12px;
}

/* Activity Item */
.spark-activity-item {
   position: relative;
   display: flex;
   align-items: flex-start;
   padding-left: 16px;
}

.spark-activity-node {
   position: absolute;
   left: -20px;
   top: 10px;
   width: 26px;
   height: 26px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 0.6875rem;
   border: 2px solid #ffffff;
   box-shadow: 0 1px 3px rgba(0,0,0,0.1);
   z-index: 2;

   &.spark-node--create { background: #dbeafe; color: #2563eb; }
   &.spark-node--update { background: #f1f5f9; color: #475569; }
   &.spark-node--sent { background: #f3e8ff; color: #9333ea; }
   &.spark-node--shared { background: #e0f2fe; color: #0284c7; }
   &.spark-node--revoked { background: #ffe4e6; color: #e11d48; }
   &.spark-node--accepted { background: #dcfce7; color: #16a34a; }
   &.spark-node--declined { background: #fee2e2; color: #dc2626; }
   &.spark-node--question { background: #fef3c7; color: #d97706; }
   &.spark-node--extended { background: #ccfbf1; color: #0d9488; }
   &.spark-node--reissue { background: #ffedd5; color: #ea580c; }
   &.spark-node--expired { background: #fee2e2; color: #dc2626; }
   &.spark-node--status { background: #e0e7ff; color: #4f46e5; }
}

/* Card */
.spark-activity-card {
   flex-grow: 1;
   background: #ffffff;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 10px 14px;
   box-shadow: 0 1px 2px rgba(0,0,0,0.03);
   transition: all 0.2s ease;

   &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 3px 8px rgba(0,0,0,0.05);
   }
}

.spark-activity-card__title {
   font-size: 0.8125rem;
   font-weight: 700;
   color: #1e293b;
}

.spark-activity-badge {
   font-size: 0.625rem;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   padding: 2px 7px;
   border-radius: 9999px;

   &.spark-badge--create { background: #eff6ff; color: #1d4ed8; }
   &.spark-badge--update { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }
   &.spark-badge--sent { background: #faf5ff; color: #7e22ce; }
   &.spark-badge--shared { background: #f0f9ff; color: #0369a1; }
   &.spark-badge--revoked { background: #fff1f2; color: #be123c; }
   &.spark-badge--accepted { background: #f0fdf4; color: #15803d; }
   &.spark-badge--declined { background: #fef2f2; color: #b91c1c; }
   &.spark-badge--question { background: #fffbeb; color: #b45309; }
   &.spark-badge--extended { background: #f0fdfa; color: #0f766e; }
   &.spark-badge--reissue { background: #fff7ed; color: #c2410c; }
   &.spark-badge--expired { background: #fef2f2; color: #b91c1c; }
   &.spark-badge--status { background: #eef2ff; color: #4338ca; }
}

/* Actor */
.spark-actor-avatar {
   width: 18px;
   height: 18px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 0.5625rem;
   font-weight: 700;

   &.spark-actor-avatar--user {
      background: #00A0C1;
      color: #ffffff;
   }

   &.spark-actor-avatar--system {
      background: #64748b;
      color: #ffffff;
   }
}

.spark-actor-name {
   font-size: 0.75rem;
}

/* Details text */
.spark-activity-details {
   font-size: 0.8125rem;
   color: #334155;
   line-height: 1.45;

   .spark-activity-details__text {
      white-space: pre-line;
      word-break: break-word;

      &.is-quote {
         padding: 8px 12px;
         background: #f8fafc;
         border-left: 3px solid #00A0C1;
         border-radius: 0 6px 6px 0;
         font-style: italic;
         color: #1e293b;
      }
   }
}

.spark-status-transition-box {
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 4px 8px;
   background: #f1f5f9;
   border-radius: 6px;

   .spark-status-badge {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 4px;

      &.from {
         background: #e2e8f0;
         color: #475569;
      }

      &.to {
         background: #dcfce7;
         color: #15803d;
      }
   }
}

/* Footer */
.spark-exact-time {
   font-size: 0.6875rem;
}

.spark-edited-flag {
   font-size: 0.6875rem;
   font-style: italic;
}

.spark-btn-copy-sm {
   background: none;
   border: none;
   color: #94a3b8;
   font-size: 0.75rem;
   padding: 2px 4px;
   border-radius: 4px;
   cursor: pointer;
   transition: all 0.15s ease;

   &:hover {
      background: #f1f5f9;
      color: #00A0C1;
   }
}

/* Empty State */
.spark-activity-empty {
   .spark-activity-empty__icon {
      font-size: 2.5rem;
      color: #cbd5e1;
   }
}

/* Skeleton Loading */
.spark-skeleton-item {
   display: flex;
   align-items: flex-start;
   gap: 12px;

   .spark-skeleton-dot {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #e2e8f0;
      flex-shrink: 0;
   }

   .spark-skeleton-card {
      flex-grow: 1;
      padding: 12px;
      border-radius: 8px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;

      .spark-skeleton-line {
         height: 10px;
         background: #e2e8f0;
         border-radius: 4px;

         &.short { width: 40%; }
         &.half { width: 60%; }
      }
   }
}
</style>
